import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaHome, FaServicestack, FaProjectDiagram, FaBlog, 
  FaBriefcase, FaInfoCircle, FaEnvelope, FaQuestionCircle,
  FaShieldAlt, FaFileContract, FaSitemap, FaUsers,
  FaCode, FaMobile, FaCloud, FaBrain, FaLock, FaRocket
} from 'react-icons/fa';
import './SitemapPage.css';

const SitemapPage = () => {
  const currentYear = new Date().getFullYear();

  const sitemapData = {
    mainPages: [
      { name: 'Home', path: '/', icon: <FaHome />, description: 'Welcome to ZustandTech' },
      { name: 'Services', path: '/services', icon: <FaServicestack />, description: 'Our software development services' },
      { name: 'Projects', path: '/projects', icon: <FaProjectDiagram />, description: 'Our portfolio and case studies' },
      { name: 'Blog', path: '/blog', icon: <FaBlog />, description: 'Latest insights and articles' },
      { name: 'Careers', path: '/careers', icon: <FaBriefcase />, description: 'Join our team' },
      { name: 'About Us', path: '/about', icon: <FaInfoCircle />, description: 'Learn about our company' },
      { name: 'Contact', path: '/contact', icon: <FaEnvelope />, description: 'Get in touch with us' }
    ],
    servicePages: [
      { name: 'Web Development', path: '/services#web-development', icon: <FaCode />, description: 'Custom web applications' },
      { name: 'Mobile Apps', path: '/services#mobile-apps', icon: <FaMobile />, description: 'iOS and Android development' },
      { name: 'Cloud Solutions', path: '/services#cloud-solutions', icon: <FaCloud />, description: 'Scalable cloud infrastructure' },
      { name: 'AI/ML', path: '/services#ai-ml', icon: <FaBrain />, description: 'AI and machine learning' },
      { name: 'Cybersecurity', path: '/services#cybersecurity', icon: <FaLock />, description: 'Security solutions' },
      { name: 'Digital Transformation', path: '/services#digital-transformation', icon: <FaRocket />, description: 'Business transformation' }
    ],
    companyPages: [
      { name: 'Our Team', path: '/team', icon: <FaUsers />, description: 'Meet our team members' },
      { name: 'FAQ', path: '/faq', icon: <FaQuestionCircle />, description: 'Frequently asked questions' },
      { name: 'Privacy Policy', path: '/privacy', icon: <FaShieldAlt />, description: 'Our privacy practices' },
      { name: 'Terms of Service', path: '/terms', icon: <FaFileContract />, description: 'Terms and conditions' },
      { name: 'Sitemap', path: '/sitemap', icon: <FaSitemap />, description: 'Website navigation guide' }
    ],
    projectPages: [
      { name: 'E-Commerce Platform', path: '/projects#ecommerce', category: 'Web Development' },
      { name: 'Healthcare App', path: '/projects#healthcare', category: 'Mobile App' },
      { name: 'AI Analytics Dashboard', path: '/projects#analytics', category: 'AI/ML' },
      { name: 'Cloud Migration Tool', path: '/projects#cloud', category: 'Cloud' }
    ],
    blogCategories: [
      { name: 'Web Development', path: '/blog?category=web-development' },
      { name: 'Mobile Apps', path: '/blog?category=mobile-apps' },
      { name: 'AI/ML', path: '/blog?category=ai-ml' },
      { name: 'Cloud Computing', path: '/blog?category=cloud' },
      { name: 'Tech News', path: '/blog?category=tech-news' }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="sitemap-page"
    >
      <section className="sitemap-content">
        <div className="container">
          {/* Page Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-heading"
          >
            <h1>Website <span className="gradient-text">Sitemap</span></h1>
            <p>Navigate through all pages and sections of ZustandTech</p>
          </motion.div>

          {/* Overview Stats */}
          <div className="sitemap-stats">
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="stat-number">{sitemapData.mainPages.length + sitemapData.servicePages.length + sitemapData.companyPages.length + sitemapData.projectPages.length + sitemapData.blogCategories.length}</span>
              <span className="stat-label">Total Pages</span>
            </motion.div>
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="stat-number">{sitemapData.mainPages.length}</span>
              <span className="stat-label">Main Pages</span>
            </motion.div>
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <span className="stat-number">{sitemapData.servicePages.length}</span>
              <span className="stat-label">Services</span>
            </motion.div>
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <span className="stat-number">{currentYear}</span>
              <span className="stat-label">Current Year</span>
            </motion.div>
          </div>

          {/* Sitemap Grid */}
          <div className="sitemap-grid">
            {/* Main Pages */}
            <motion.div 
              className="sitemap-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaHome className="section-icon" />
                Main Pages
              </h2>
              <div className="page-list">
                {sitemapData.mainPages.map((page, index) => (
                  <motion.div
                    key={page.path}
                    className="page-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link to={page.path} className="page-link">
                      <span className="page-icon">{page.icon}</span>
                      <div className="page-info">
                        <span className="page-name">{page.name}</span>
                        <span className="page-description">{page.description}</span>
                      </div>
                      <span className="page-path">{page.path}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Services */}
            <motion.div 
              className="sitemap-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaServicestack className="section-icon" />
                Our Services
              </h2>
              <div className="page-list">
                {sitemapData.servicePages.map((page, index) => (
                  <motion.div
                    key={page.path}
                    className="page-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <a href={page.path} className="page-link">
                      <span className="page-icon">{page.icon}</span>
                      <div className="page-info">
                        <span className="page-name">{page.name}</span>
                        <span className="page-description">{page.description}</span>
                      </div>
                      <span className="page-path">{page.path}</span>
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Company Pages */}
            <motion.div 
              className="sitemap-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaInfoCircle className="section-icon" />
                Company & Legal
              </h2>
              <div className="page-list">
                {sitemapData.companyPages.map((page, index) => (
                  <motion.div
                    key={page.path}
                    className="page-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link to={page.path} className="page-link">
                      <span className="page-icon">{page.icon}</span>
                      <div className="page-info">
                        <span className="page-name">{page.name}</span>
                        <span className="page-description">{page.description}</span>
                      </div>
                      <span className="page-path">{page.path}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Project Categories */}
            <motion.div 
              className="sitemap-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaProjectDiagram className="section-icon" />
                Featured Projects
              </h2>
              <div className="page-list compact">
                {sitemapData.projectPages.map((project, index) => (
                  <motion.div
                    key={project.name}
                    className="page-item compact"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <a href={project.path} className="page-link compact">
                      <span className="project-category-badge">{project.category}</span>
                      <span className="page-name">{project.name}</span>
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Blog Categories */}
            <motion.div 
              className="sitemap-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaBlog className="section-icon" />
                Blog Categories
              </h2>
              <div className="page-list compact">
                {sitemapData.blogCategories.map((category, index) => (
                  <motion.div
                    key={category.name}
                    className="page-item compact"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link to={category.path} className="page-link compact">
                      <span className="page-name">{category.name}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div 
              className="sitemap-section quick-links"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title-with-icon">
                <FaRocket className="section-icon" />
                Quick Links
              </h2>
              <div className="quick-links-grid">
                <Link to="/contact" className="quick-link-card">
                  <FaEnvelope />
                  <span>Contact Sales</span>
                </Link>
                <Link to="/careers" className="quick-link-card">
                  <FaBriefcase />
                  <span>Join Us</span>
                </Link>
                <Link to="/blog" className="quick-link-card">
                  <FaBlog />
                  <span>Latest Posts</span>
                </Link>
                <Link to="/faq" className="quick-link-card">
                  <FaQuestionCircle />
                  <span>FAQ</span>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* XML Sitemap Notice */}
          <motion.div 
            className="xml-sitemap-notice"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <p>
              <strong>XML Sitemap:</strong> For search engines, we also provide an XML sitemap at{' '}
              <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer">/sitemap.xml</a>
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default SitemapPage;